import { useState, useEffect, useCallback } from "react";
import { useLocation as useRouterLocation } from "react-router-dom";
import { discovery } from "../lib/api";
import { useLocation } from "../contexts/LocationContext";
import FilterBar from "../components/posts/FilterBar";
import PostCard from "../components/posts/PostCard";
import Pagination from "../components/common/Pagination";
import LoadingOverlay from "../components/LoadingOverlay";
import { MapPin } from "lucide-react";

export default function FeedPage() {
  const routerLocation = useRouterLocation();
  const { latitude, longitude, error: locError, requestLocation } = useLocation();
  const [filters, setFilters] = useState({ radius: 50, status: "all" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (latitude === null) requestLocation();
  }, [latitude, requestLocation]);

  const fetchFeed = useCallback(async () => {
    if (!latitude || !longitude) return;
    setLoading(true);
    setError(null);
    try {
      const result = await discovery.feed({
        latitude,
        longitude,
        page,
        limit: 20,
        ...filters,
      });
      setData(result);
    } catch (err) {
      setError("Failed to load feed");
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude, page, filters, routerLocation.key]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  if (!latitude && !locError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <MapPin size={48} className="text-walnut-light mb-4" />
        <p className="text-walnut-light">Requesting your location...</p>
      </div>
    );
  }

  if (locError && !latitude) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <MapPin size={48} className="text-coral mb-4" />
        <p className="text-walnut mb-2">Location access is needed to show nearby posts</p>
        <p className="text-sm text-walnut-light mb-4">{locError}</p>
        <button
          onClick={requestLocation}
          className="px-4 py-2 bg-terracotta text-white rounded-md text-sm font-medium hover:bg-terracotta-dark transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <FilterBar filters={filters} onChange={handleFilterChange} />
      {loading && <LoadingOverlay />}
      {error && <p className="text-center text-coral py-8">{error}</p>}
      {data && !loading && (
        <>
          {data.posts.length === 0 ? (
            <p className="text-center text-walnut-light py-8">No posts found. Try adjusting your filters.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
          )}
          <Pagination
            page={data.pagination.page}
            totalPages={data.pagination.total_pages}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
